import { Timestamp } from 'firebase/firestore';

export interface ServiceArea {
  id: string;
  name: string;
  centerLat: number;
  centerLng: number;
  radiusKm: number;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface WeightPrice {
  weight: string;
  price: number;
  originalPrice?: number;
  stockQuantity: number;
}
export interface Product {
  id: string;
  name: string;
  description: string;
  category: string; // Legacy field for backward compatibility
  categoryId: string; // Category document ID
  categoryName: string; // Category display name
  price: number;
  originalPrice?: number;
  unit: string;
  stockQuantity: number;
  weightPrices?: WeightPrice[];
  pricePerKg?: number;
  images: string[];
  primaryImage?: string;
  isAvailable: boolean;
  isOrganic: boolean;
  isSeasonal: boolean;
  rating: number;
  reviewCount: number;
  nutritionInfo?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
  preOrderOnly: boolean;
  estimatedDelivery?: string;
  minOrderQuantity?: number;
  // Extended product details
  healthBenefits?: string;
  storageTemperature?: string;
  shelfLife?: string;
  disclaimer?: string;
  customerCareEmail?: string;
  countryOfOrigin?: string;
  returnPolicy?: string;
  storageTips?: string;
}

// Order Status Enums
export enum OrderStatus {
  PENDING = 'pending',
  CONFIRMED = 'confirmed',
  DELIVERY_ASSIGNED = 'deliveryAssigned',
  OUT_FOR_DELIVERY = 'outForDelivery',
  DELIVERED = 'delivered',
  CANCELLED = 'cancelled',
  RETURNED = 'returned',
  REFUNDED = 'refunded'
}

export enum PaymentStatus {
  PENDING = 'pending',
  PAID = 'paid',
  FAILED = 'failed',
  REFUNDED = 'refunded'
}

export enum PaymentMethod {
  COD = 'cod',
  BANK_QR = 'bankQr',
  CARD = 'card',
  UPI = 'upi',
  NETBANKING = 'netbanking'
}

export interface OrderStatusHistory {
  status: OrderStatus;
  timestamp: Date;
  updatedBy: string;
  notes?: string;
  previousStatus?: OrderStatus;
}

// Cart Item
export interface CartItem {
  id: string;
  productId?: string; // Optional for backward compatibility
  productName?: string; // Optional for backward compatibility
  productImage?: string;
  price?: number;
  quantity: number;
  unit?: string;
  totalPrice?: number;
  product?: Product;
  unitPrice?: number;
  selectedWeight?: {
    weight: string;
    price: number;
    originalPrice?: number;
    stockQuantity: number;
  };
  addedAt?: string; // When item was added to cart
}

// Address
export interface Address {
  id: string;
  title: string; // Address title/name (e.g., "Home", "Office", "Pune")
  fullAddress: string; // Complete formatted address
  wingFlatNumber?: string; // Wing/Flat/Apartment number
  area?: string; // Area/Street/Avenue
  city: string;
  state: string;
  pincode: string;
  latitude?: number; // GPS coordinates
  longitude?: number; // GPS coordinates
  isDefault: boolean;
  name?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  landmark?: string;
}

export interface Order {
  id: string;
  userId: string;
  customer?: {
    id: string;
    name: string;
    email: string;
    mobile: string;
    profileImage?: string | null;
  };
  items: CartItem[];
  deliveryAddress: Address;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  paymentMethod: PaymentMethod;
  totalAmount: number;
  deliveryFee: number;
  discount: number;
  orderDate: Date;
  deliveryDate: Date;
  deliveryTimeSlot?: string;
  cancelledAt?: Date;
  notes?: string;
  trackingId?: string;
  cancellationReason?: string;
  statusHistory?: OrderStatusHistory[]; // Audit trail for status changes
  assignedDeliveryAgent?: string; // ID of assigned delivery agent (deprecated - use deliveryAgent)
  deliveryAgent?: {
    id: string;
    name: string;
    phone: string;
    email: string;
    profileImage?: string; // Optional - only included if agent has a profile image
    vehicleType: VehicleType;
    vehicleNumber: string;
  };
}

// Delivery Person Status
export enum DeliveryPersonStatus {
  AVAILABLE = 'available',
  BUSY = 'busy',
  OFFLINE = 'offline'
}

export enum VehicleType {
  BICYCLE = 'bicycle',
  MOTORCYCLE = 'motorcycle',
  CAR = 'car',
  VAN = 'van'
}

// Delivery Person Model
export interface DeliveryPerson {
  id: string;
  name: string;
  email: string;
  phone: string;
  profileImage?: string;
  status: DeliveryPersonStatus;
  vehicleType: VehicleType;
  vehicleNumber: string;
  licenseNumber: string;
  rating: number;
  totalDeliveries: number;
  totalEarnings: number;
  joinDate: Date;
  isVerified: boolean;
  isActive: boolean;
  currentLocation?: string;
  latitude?: number;
  longitude?: number;
  lastLocationUpdate?: Date;
  assignedAreas: string[];
}

// User Roles
export enum UserRole {
  ADMIN = 'admin',
  DELIVERY = 'delivery',
  END_USER = 'end_user'
}

// User Model with Role-based Authentication
export interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: UserRole;
  profileImage?: string;
  addresses: Address[];
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  // Role-specific data references
  deliveryPersonId?: string; // If role is DELIVERY, reference to DeliveryPerson document
  adminPermissions?: string[]; // If role is ADMIN, specific permissions
}

// Category Model
export interface Category {
  id: string;
  name: string;
  description?: string;
  image?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: Date;
  updatedAt: Date;
}

// Firebase document types (for database operations)
export interface CategoryDocument {
  id: string;
  name: string;
  description?: string;
  image?: string;
  isActive: boolean;
  sortOrder: number;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Analytics Data
export interface DashboardStats {
  totalOrders: number;
  totalRevenue: number;
  totalProducts: number;
  totalDeliveryPersons: number;
  todayOrders: number;
  todayRevenue: number;
  pendingOrders: number;
  activeDeliveryPersons: number;
}

export interface OrderAnalytics {
  date: string;
  orders: number;
  revenue: number;
}

// Form Data Types
export interface ProductFormData {
  name: string;
  description: string;
  category: string; // Legacy field for backward compatibility
  categoryId: string; // Category document ID
  categoryName: string; // Category display name
  // Legacy pricing (for backward compatibility)
  price: number;
  originalPrice?: number;
  unit: string;
  stockQuantity: number;
  // New weight-based pricing
  pricePerKg?: number;
  weightPrices?: WeightPrice[];
  // Images and other fields
  images: string[];
  primaryImage?: string;
  isAvailable: boolean;
  isOrganic: boolean;
  isSeasonal: boolean;
  nutritionInfo?: Record<string, any>;
  // Pre-order specific fields
  preOrderOnly: boolean;
  estimatedDelivery?: string;
  minOrderQuantity?: number;
  // Extended product details
  healthBenefits?: string;
  storageTemperature?: string;
  shelfLife?: string;
  disclaimer?: string;
  customerCareEmail?: string;
  countryOfOrigin?: string;
  returnPolicy?: string;
  storageTips?: string;
}

export interface DeliveryPersonFormData {
  name: string;
  email: string;
  phone: string;
  password: string; // For creating Firebase Auth user
  vehicleType: VehicleType;
  vehicleNumber: string;
  licenseNumber: string;
  assignedAreas: string[];
}

export interface UserFormData {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: UserRole;
  profileImage?: string;
  isActive: boolean;
}

export interface CategoryFormData {
  name: string;
  description?: string;
  image?: string;
  isActive: boolean;
  sortOrder: number;
}
